// RTT distribution: histogram + CDF (owned by the web:charts agent).
//
// Canvas histogram of echoed round-trip times overlaid with the cumulative
// distribution (right axis, 0..100%). Vertical markers call out p50 / p90 / p99
// and the acceptable-delay threshold so the tail is legible at a glance. A
// visually-hidden table gives the same numbers to assistive tech.

import { useCallback, useMemo } from 'react';
import type { RunResult } from '@/engine/types';
import { CanvasSurface, EmptyNote } from './Canvas';
import { useChartTheme, withAlpha, type ChartPalette } from './palette';
import { srOnly } from './a11y';

export interface RttDistributionProps {
  run: RunResult;
  height?: number;
}

interface Hist {
  rtts: number[];
  min: number;
  max: number;
  bins: number[];
  binW: number;
  cdf: number[];
}

function buildHist(run: RunResult): Hist {
  const rtts: number[] = [];
  for (const p of run.packets) {
    if (p.rttMs != null && (p.cls === 'ok' || p.cls === 'late')) rtts.push(p.rttMs);
  }
  rtts.sort((a, b) => a - b);
  const n = rtts.length;
  if (n === 0) return { rtts, min: 0, max: 1, bins: [], binW: 1, cdf: [] };
  const min = rtts[0];
  const max = Math.max(rtts[n - 1], min + 1e-6);
  const nb = Math.min(48, Math.max(8, Math.round(Math.sqrt(n))));
  const binW = (max - min) / nb;
  const bins = new Array<number>(nb).fill(0);
  for (const r of rtts) {
    const idx = Math.min(nb - 1, Math.floor((r - min) / binW));
    bins[idx] += 1;
  }
  const cdf = new Array<number>(nb).fill(0);
  let acc = 0;
  for (let i = 0; i < nb; i++) {
    acc += bins[i];
    cdf[i] = acc / n;
  }
  return { rtts, min, max, bins, binW, cdf };
}

export function RttDistribution({ run, height = 240 }: RttDistributionProps) {
  const { palette } = useChartTheme();
  const hist = useMemo(() => buildHist(run), [run]);
  const m = run.metrics;
  const accept = run.settings.acceptableDelayMs;

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      drawHist(ctx, w, h, hist, palette, [
        { v: m.p50, label: 'p50', color: palette.accent },
        { v: m.p90, label: 'p90', color: palette.late },
        { v: m.p99, label: 'p99', color: palette.lostUp },
        { v: accept, label: 'acc', color: palette.threshold, dashed: true },
      ]);
    },
    [hist, palette, m.p50, m.p90, m.p99, accept],
  );

  if (hist.rtts.length === 0) return <EmptyNote label="No echoed packets to distribute." />;

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <CanvasSurface height={height} draw={draw} ariaLabel="RTT histogram and CDF" />
      </div>
      <table style={srOnly}>
        <caption>RTT distribution summary (ms)</caption>
        <tbody>
          <tr>
            <th scope="row">p50</th>
            <td>{m.p50.toFixed(2)}</td>
          </tr>
          <tr>
            <th scope="row">p90</th>
            <td>{m.p90.toFixed(2)}</td>
          </tr>
          <tr>
            <th scope="row">p99</th>
            <td>{m.p99.toFixed(2)}</td>
          </tr>
          <tr>
            <th scope="row">min</th>
            <td>{m.minRtt.toFixed(2)}</td>
          </tr>
          <tr>
            <th scope="row">max</th>
            <td>{m.maxRtt.toFixed(2)}</td>
          </tr>
          <tr>
            <th scope="row">acceptable</th>
            <td>{accept.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </figure>
  );
}

interface Marker {
  v: number;
  label: string;
  color: string;
  dashed?: boolean;
}

function drawHist(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  hist: Hist,
  palette: ChartPalette,
  markers: Marker[],
) {
  const padL = 34;
  const padR = 40;
  const padT = 8;
  const padB = 22;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const maxCount = Math.max(1, ...hist.bins);
  const xOf = (val: number) => padL + ((val - hist.min) / (hist.max - hist.min)) * plotW;

  // Axes.
  ctx.strokeStyle = palette.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padL, padT);
  ctx.lineTo(padL, padT + plotH);
  ctx.lineTo(padL + plotW, padT + plotH);
  ctx.stroke();

  // Bars.
  const bw = plotW / hist.bins.length;
  ctx.fillStyle = withAlpha(palette.accent, 0.55);
  hist.bins.forEach((c, i) => {
    const barH = (c / maxCount) * plotH;
    ctx.fillRect(padL + i * bw + 0.5, padT + plotH - barH, Math.max(1, bw - 1), barH);
  });

  // CDF line (right axis 0..1).
  ctx.strokeStyle = palette.text;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  hist.cdf.forEach((c, i) => {
    const x = padL + (i + 0.5) * bw;
    const y = padT + plotH - c * plotH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Markers.
  ctx.font = '10px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  markers.forEach((mk) => {
    const x = xOf(mk.v);
    if (x < padL || x > padL + plotW) return;
    ctx.strokeStyle = mk.color;
    ctx.setLineDash(mk.dashed ? [4, 3] : []);
    ctx.beginPath();
    ctx.moveTo(x, padT);
    ctx.lineTo(x, padT + plotH);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = mk.color;
    ctx.textAlign = 'left';
    ctx.fillText(mk.label, x + 2, padT + 1);
  });

  // Axis labels.
  ctx.fillStyle = palette.textMuted;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(`${hist.min.toFixed(1)} ms`, padL, padT + plotH + 5);
  ctx.fillText(`${hist.max.toFixed(1)} ms`, padL + plotW, padT + plotH + 5);
  ctx.textAlign = 'right';
  ctx.fillText('100%', padL + plotW + padR - 2, padT);
  ctx.fillText('0%', padL + plotW + padR - 2, padT + plotH - 8);
}
